import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as gameService from '../services/gameService';

const Play = () => {
  const { fileId } = useParams();
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameTitle, setGameTitle] = useState('Untitled Game');
  const iframeRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGameHtml = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Check if this is a locally stored game first (starts with 'local-game-')
        if (fileId.startsWith('local-game-')) {
          console.log(`Loading locally stored game: ${fileId}`);
          const localGame = localStorage.getItem(`game_${fileId}`);
          
          if (localGame) {
            const gameData = JSON.parse(localGame);
            setHtmlContent(gameData.content);
            setGameTitle(gameData.title || 'Untitled Game');
            setLoading(false);
            return;
          } else {
            throw new Error('Local game not found');
          }
        }
        
        // If not a local game, try to fetch from server
        // First check if server is available
        const isServerAvailable = await gameService.checkServerAvailability();
        
        if (!isServerAvailable) {
          throw new Error('Server is currently unavailable');
        }
        
        // Fetch the game data
        const gameData = await gameService.fetchGameById(fileId);
        setHtmlContent(gameData.content);
        setGameTitle(gameData.title || 'Untitled Game');
        setLoading(false);
      } catch (err) {
        console.error('Error fetching game:', err);
        
        // Display a more user-friendly error message
        if (err.message.includes('NetworkError') || err.message.includes('Failed to fetch')) {
          setError('Unable to connect to the server. Please check your internet connection or try again later.');
        } else {
          setError(`${err.message}. The server might be temporarily unavailable.`);
        }
        
        setLoading(false);
      }
    };

    fetchGameHtml();
  }, [fileId]);

  useEffect(() => {
    if (htmlContent && !loading && iframeRef.current) {
      try {
        const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        
        // Set page title
        document.title = `${gameTitle} - Play`;
      } catch (err) {
        console.error('Error writing to iframe:', err);
        setError('Failed to render game content. Please try again.');
      }
    }
  }, [htmlContent, loading, gameTitle]);

  const handleBack = () => {
    navigate('/');
  };

  if (loading) {
    return <LoadingContainer>Loading game...</LoadingContainer>;
  }

  if (error) {
    return (
      <ErrorContainer>
        <ErrorMessage>Error: {error}</ErrorMessage>
        <BackButton onClick={handleBack}>Go Back</BackButton>
      </ErrorContainer>
    );
  }

  return (
    <GameContainer>
      {htmlContent ? (
        <>
          <GameHeader>
            <BackButton onClick={handleBack}>Back to Games</BackButton>
            <GameTitle>{gameTitle}</GameTitle>
          </GameHeader>
          <GameFrame 
            ref={iframeRef}
            title="Game Content"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          />
        </>
      ) : (
        <ErrorContainer>
          <ErrorMessage>No game content found.</ErrorMessage>
          <BackButton onClick={handleBack}>Go Back</BackButton>
        </ErrorContainer>
      )}
    </GameContainer>
  );
};

const GameContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #000;
`;

const GameHeader = styled.div`
  background-color: #111;
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const GameTitle = styled.h1`
  color: white;
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  font-family: 'Space Grotesk', sans-serif;
`;

const GameFrame = styled.iframe`
  flex: 1;
  width: 100%;
  height: calc(100vh - 50px);
  border: none;
  background-color: #000;
`;

const BackButton = styled.button`
  padding: 8px 16px;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background-color: #555;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 24px;
  color: #fff;
  background-color: #000;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  padding: 20px;
  text-align: center;
  background-color: #000;
`;

const ErrorMessage = styled.div`
  font-size: 20px;
  color: #f44336;
  margin-bottom: 20px;
`;

export default Play; 